/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { Route, Switch, Redirect, BrowserRouter as Router, withRouter } from 'react-router-dom';

import Loading from 'components/Loading';
import { useSelector } from 'react-redux';
import { Locations, LocationsList, LocationDetails } from 'pages/Locations';

import { VehiclesList, VehicleDetails, VehiclesOnboarding } from 'pages/Vehicles';

import AccountPage from 'pages/Account';
import { OrganizationDetails } from 'pages/Organizations';
import { BrokerList } from 'pages/Brokers';
import DashboardPage from 'pages/Dashboard';
import { NotFound } from 'pages/ErrorPages';
import { UsersList, UserDetails } from 'pages/Users';
import { AlarmsList, AlarmsDetails } from 'pages/Alarms';
import { CustomersList, CustomersDetails } from 'pages/Customers';
import { ReservationsList, ReservationDetails } from 'pages/Reservations';
import {
  SubscriptionsList,
  SubscriptionDetails,
  SubscriptionsPeriodsList,
} from 'pages/Subscriptions';
import { UserGroupsList, UserGroupsOnboarding, UserGroupDetails } from 'pages/UserGroups';
import { DealerList, DealerOnboarding, DealerDetails } from 'pages/Dealer';

import { SkusList, SkuDetails, SkusOnboarding } from 'pages/Skus';

import {
  VehiclesGroupsList,
  VehiclesGroupsDetails,
  VehiclesGroupsOnboarding,
} from 'pages/VehiclesGroups';

import { FleetsList, FleetsDetails, FleetsOnboarding } from 'pages/Fleets';

import {
  VehiclesTypesList,
  VehicleTypeDetails,
  VehiclesTypesOnboarding,
} from 'pages/VehiclesTypes';

import {
  LoginPage,
  ConfirmEmail,
  ChangePassword,
  ForgotPassword,
  MultiFactorAuthentication,
  Unauthorized,
} from 'pages/Auth';

import { InvoiceList, InvoicePage, InvoiceDetails, InvoiceWizard } from 'pages/Invoice';
import { BillingDetails, BillingPreview, BillingList } from 'pages/Billing';
import { PaymentList, PaymentDetails } from 'pages/Payment';
import { DocumentsList } from 'pages/Documents';
import { RulesetList, RulesetCreate, RulesetDetails } from 'pages/Rulesets';
import { TaxRuleset, TaxRulesetList, TaxRulesetDetails } from 'pages/TaxRulesets';
import { BusinessHours, BusinessHoursList, BusinessHoursDetails } from 'pages/BusinessHours';
import { TasksList, TasksDetails } from 'pages/Tasks';
import VersionsPage from 'pages/Versions/VersionsPage';
import DocumentTypesList from 'pages/DocumentTypes/DocumentTypesList';
import VersionsDetailsPage from 'pages/Versions/VersionsDetailsPage';
import DocumentsDetailsPage from 'pages/Documents/DocumentsDetailsPage';

import {
  ReservationRulesetList,
  ReservationRulesetDetails,
  ReservationRulesetCreate,
} from 'pages/ReservationRulesets';

import ReportDownload from 'pages/Reports/ReportDownload';

const ReportList = React.lazy(() => import('pages/Reports/List/ReportList'));
const ReportCreate = React.lazy(() => import('pages/Reports/ReportCreate'));
const ReportsDocumentation = React.lazy(() => import('pages/Reports/ReportsDocumentation'));

import { PromoCodeList, PromoCodeCreate, PromoCodeDetails } from 'pages/PromoCodes';

import { PartnersList, PartnerDetails } from 'pages/Partners';

import { ChartList } from 'pages/Charts';

import { CreditTypeList, CreditTypeDetails } from 'pages/CreditTypes';

import { NotificationsList } from 'pages/Notifications';

import { AccountStatementList, AccountStatementDetails } from '../pages/AccountStatement';

import { NFCList, NFCDetails } from 'pages/NFC';

import Authentication from 'pages/Authentication';

/**
 * Handle private/authenticated routes
 * @param {Boolean} isAuthenticated
 * @param {node} children
 */
const PrivateRoute = ({ isAuthenticated, children, ...rest }) => (
  <Route
    {...rest}
    render={({ location }) =>
      isAuthenticated ? (
        children
      ) : (
        <Redirect
          to={{
            pathname: '/login',
            state: { from: location },
          }}
        />
      )
    }
  />
);

const ScrollToTop = withRouter(({ history }) => {
  useEffect(() => {
    const unlisten = history.listen(() => {
      window.scrollTo(0, 0);
    });
    return () => {
      unlisten();
    };
    // eslint-disable-next-line
  }, []);

  return null;
});

// Create router
const AppRouter = () => {
  const { isAuthenticated, fetchingUser } = useSelector((state) => state.auth);
  const { fetchingPermissions } = useSelector((state) => state.user);
  const isLoading = fetchingUser || fetchingPermissions;

  if (isLoading) {
    return (
      <Router>
        <Switch>
          <Route component={Loading} />
        </Switch>
      </Router>
    );
  }

  return (
    <Router>
      <>
        <ScrollToTop />
        <Switch>
          <Route path="/login">
            <LoginPage />
          </Route>
          <Route path="/pages/password/request">
            <ForgotPassword />
          </Route>
          <Route path="/pages/password/change">
            <ChangePassword />
          </Route>
          <Route path="/pages/email/confirm">
            <ConfirmEmail />
          </Route>
          <Route path="/mfa">
            <MultiFactorAuthentication />
          </Route>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/404">
            <Unauthorized />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/documents/:documentId/versions/:id"
          >
            <VersionsDetailsPage />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/documents/:documentId/versions"
          >
            <VersionsPage />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/documents/:id">
            <DocumentsDetailsPage />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/documents">
            <DocumentsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/document-types">
            <DocumentTypesList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/service-groups/create">
            <VehiclesGroupsOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/service-groups/:id">
            <VehiclesGroupsDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/service-groups">
            <VehiclesGroupsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/fleets/create">
            <FleetsOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/fleets/:id">
            <FleetsDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/fleets">
            <FleetsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicle-types/create">
            <VehiclesTypesOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicle-types/:id/update">
            <VehiclesTypesOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicle-types/:id">
            <VehicleTypeDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicle-types">
            <VehiclesTypesList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/alarms/:id">
            <AlarmsDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/alarms">
            <AlarmsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/dashboard">
            <DashboardPage />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/locations/create">
            <Locations />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/locations/:id/update">
            <Locations />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/locations/:id">
            <LocationDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/locations">
            <LocationsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicles/create">
            <VehiclesOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicles/:id/update">
            <VehiclesOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicles/:id">
            <VehicleDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/vehicles">
            <VehiclesList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/employees/:id">
            <UserDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/employees">
            <UsersList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/customers/:id">
            <CustomersDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/customers">
            <CustomersList />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/subscriptions/reservation-periods"
          >
            <SubscriptionsPeriodsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/subscriptions/:id">
            <SubscriptionDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/subscriptions">
            <SubscriptionsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reservations/:id">
            <ReservationDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reservations">
            <ReservationsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/skus/create">
            <SkusOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/skus/:id/update">
            <SkusOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/skus/:id">
            <SkuDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/skus">
            <SkusList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/pricing-rulesets/create">
            <RulesetCreate />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/pricing-rulesets/:id/:action">
            <RulesetCreate />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/pricing-rulesets/:id">
            <RulesetDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/pricing-rulesets">
            <RulesetList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/tax-rulesets/create">
            <TaxRuleset />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/tax-rulesets/:id/:action">
            <TaxRuleset />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/tax-rulesets/:id">
            <TaxRulesetDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/tax-rulesets">
            <TaxRulesetList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reservation-rulesets/create">
            <ReservationRulesetCreate />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/reservation-rulesets/:id([0-9]*)/:action"
          >
            <ReservationRulesetCreate />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/reservation-rulesets/:id([0-9]*)"
          >
            <ReservationRulesetDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reservation-rulesets">
            <ReservationRulesetList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/users-groups/create">
            <UserGroupsOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/dealers/create">
            <DealerOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/users-groups/:id/update">
            <UserGroupsOnboarding />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/users-groups/:id">
            <UserGroupDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/dealers/:id">
            <DealerDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/users-groups">
            <UserGroupsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/dealers">
            <DealerList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/tasks/:id">
            <TasksDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/tasks">
            <TasksList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/invoices/create">
            <InvoiceWizard />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/invoices/:id/preview">
            <InvoicePage />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/billing/:id/preview">
            <BillingPreview />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/invoices/:id/:action">
            <InvoiceWizard />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/invoices/:id">
            <InvoiceDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/billing/:id">
            <BillingDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/invoices">
            <InvoiceList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/billing">
            <BillingList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/statement-entries/create">
            <InvoiceWizard isStatementEntry />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/statement-entries/:id/preview"
          >
            <InvoicePage isStatementEntry isBill />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/statement-entries/:id/:action"
          >
            <InvoiceWizard isStatementEntry />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/statement-entries/:id">
            <InvoiceDetails isStatementEntry />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/statement-entries">
            <InvoiceList isStatementEntry />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/bills/:id">
            <InvoicePage isBill />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/bills/:id">
            <InvoicePage isBill />
          </PrivateRoute>
          <Redirect from="/:org/bills" to="/:org/invoices" />
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/account">
            <AccountPage />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/general-information">
            <OrganizationDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/brokers">
            <BrokerList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/payments/:id">
            <PaymentDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/payments">
            <PaymentList />
          </PrivateRoute>

          {/** Reports */}
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/legacy-reports/docs/:type">
            <React.Suspense fallback={<>...</>}>
              <ReportsDocumentation page="legacy-reports" key="reports-docs-type" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/legacy-reports/docs">
            <React.Suspense fallback={<>...</>}>
              <ReportsDocumentation page="legacy-reports" key="reports-docs" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/legacy-reports/create">
            <React.Suspense fallback={<>...</>}>
              <ReportCreate type="legacy-reports" key="reports-create" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/legacy-reports/:id/clone">
            <React.Suspense fallback={<>...</>}>
              <ReportCreate type="legacy-reports" clone key="reports-clone" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/legacy-reports/:id">
            <React.Suspense fallback={<>...</>}>
              <ReportCreate type="legacy-reports" key="reports-details" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/legacy-reports">
            <React.Suspense fallback={<>...</>}>
              <ReportList type="legacy-reports" key="reports" />
            </React.Suspense>
          </PrivateRoute>
          <Route path="/:org/report">
            <ReportDownload></ReportDownload>
          </Route>

          {/** Analytics */}
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reporting/docs/:type">
            <React.Suspense fallback={<>...</>}>
              <ReportsDocumentation page="reporting" key="analytics-docs-type" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reporting/docs">
            <React.Suspense fallback={<>...</>}>
              <ReportsDocumentation page="reporting" key="analytics-docs" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reporting/create">
            <React.Suspense fallback={<>...</>}>
              <ReportCreate type="reporting" key="analytics-create" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reporting/:id/clone">
            <React.Suspense fallback={<>...</>}>
              <ReportCreate type="reporting" clone key="analytics-clone" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reporting/:id">
            <React.Suspense fallback={<>...</>}>
              <ReportCreate type="reporting" key="analytics-details" />
            </React.Suspense>
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/reporting">
            <React.Suspense fallback={<>...</>}>
              <ReportList type="reporting" key="analytics" />
            </React.Suspense>
          </PrivateRoute>

          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/business-hours/create">
            <BusinessHours />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/business-hours/:id/:action">
            <BusinessHours />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/business-hours/:id">
            <BusinessHoursDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/business-hours">
            <BusinessHoursList />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/promo-codes/:id([0-9]*)/clone"
          >
            <PromoCodeCreate clone />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/promo-codes/:id([0-9]*)">
            <PromoCodeDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/promo-codes/create">
            <PromoCodeCreate />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/promo-codes">
            <PromoCodeList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/partners/:id([0-9]*)">
            <PartnerDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/partners">
            <PartnersList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/notifications">
            <NotificationsList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/charts">
            <ChartList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/credit-types/:id([0-9]*)">
            <CreditTypeDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/credit-types">
            <CreditTypeList />
          </PrivateRoute>
          <PrivateRoute
            isAuthenticated={isAuthenticated}
            path="/:org/account-statement/:id([0-9]*)"
          >
            <AccountStatementDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/NFC/:id([0-9]*)">
            <NFCDetails />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/NFC">
            <NFCList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/account-statement">
            <AccountStatementList />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org/authentication">
            <Authentication />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/:org">
            <DashboardPage />
          </PrivateRoute>
          <PrivateRoute isAuthenticated={isAuthenticated} path="/">
            <DashboardPage />
          </PrivateRoute>
          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </>
    </Router>
  );
};

export default AppRouter;
